import React from "react";
import { useDispatch } from "react-redux";

import Button from "@material-ui/core/Button";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "src/components/Dialog";
import { StatefulLoadableButton } from "src/components/LoadableButton";
import { formatMonthYear } from "src/utils/monthNames";
import type { ResumeSyncStatus } from "src/store/integrationSync/types";
import {
  depopDismissResume,
  depopSyncInventory,
  depopSyncSales,
} from "src/store/integrationSync/actions/depop";
import {
  vintedDismissResume,
  vintedSyncInventory,
  vintedSyncSales,
} from "src/store/integrationSync/actions/vinted";
import usePrevious from "src/utils/usePrevious";

export type ResumeOrStartImportPlatform = "vinted" | "depop";

export interface ResumeOrStartImportDialogProps {
  open: boolean;
  offer?: ResumeSyncStatus;
  platform?: ResumeOrStartImportPlatform;
  /** Props forwarded to the normal Import dialog when the user starts over. */
  importProps?: Record<string, unknown>;
  onClose: () => void;
  onStartOver: (importProps: Record<string, unknown>) => void;
}

function resumeDescription(offer: ResumeSyncStatus): string {
  if (offer.job === "sales-import" && offer.start && offer.end) {
    return `a sales import (${formatMonthYear(offer.start)}–${formatMonthYear(
      offer.end
    )})`;
  }
  if (offer.job === "inventory-import") {
    return "an inventory import";
  }
  return "an import";
}

function ResumeOrStartImportDialogContent({
  offer,
  platform,
  importProps,
  onClose,
  onStartOver,
}: {
  offer: ResumeSyncStatus;
  platform: ResumeOrStartImportPlatform;
  importProps?: Record<string, unknown>;
  onClose: () => void;
  onStartOver: (importProps: Record<string, unknown>) => void;
}) {
  const dispatch = useDispatch();
  const platformLabel = platform === "vinted" ? "Vinted" : "Depop";

  return (
    <>
      <DialogTitle>Interrupted {platformLabel} import</DialogTitle>
      <DialogContent>
        <DialogContentText>
          There is an interrupted {platformLabel} {resumeDescription(offer)}.
          Would you like to resume where it left off, or discard that progress
          and start a new import?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <StatefulLoadableButton
          onClick={async () => {
            if (platform === "depop") {
              await dispatch(
                depopDismissResume(offer.integrationId, offer.job)
              );
            } else {
              await dispatch(
                vintedDismissResume(offer.integrationId, offer.job)
              );
            }
            onStartOver(importProps || {});
          }}
        >
          Start over
        </StatefulLoadableButton>
        <StatefulLoadableButton
          variant="contained"
          color="primary"
          autoFocus
          onClick={async () => {
            if (platform === "depop") {
              if (offer.job === "sales-import" && offer.start && offer.end) {
                await dispatch(
                  depopSyncSales(offer.integrationId, {
                    start: offer.start,
                    end: offer.end,
                    resume: true,
                  })
                );
              } else if (offer.job === "inventory-import") {
                await dispatch(
                  depopSyncInventory(offer.integrationId, { resume: true })
                );
              }
            } else if (
              offer.job === "sales-import" &&
              offer.start &&
              offer.end
            ) {
              await dispatch(
                vintedSyncSales(offer.integrationId, {
                  start: offer.start,
                  end: offer.end,
                  resume: true,
                })
              );
            } else if (offer.job === "inventory-import") {
              await dispatch(
                vintedSyncInventory(offer.integrationId, { resume: true })
              );
            }
            onClose();
          }}
        >
          Resume
        </StatefulLoadableButton>
      </DialogActions>
    </>
  );
}

function ResumeOrStartImportDialog({
  open,
  offer,
  platform,
  importProps,
  onClose,
  onStartOver,
}: ResumeOrStartImportDialogProps) {
  const prev = usePrevious(open ? { offer, platform, importProps } : undefined);
  const active = open ? { offer, platform, importProps } : prev;

  return (
    <Dialog open={open} onClose={onClose}>
      {active?.offer && active.platform ? (
        <ResumeOrStartImportDialogContent
          offer={active.offer}
          platform={active.platform}
          importProps={active.importProps}
          onClose={onClose}
          onStartOver={onStartOver}
        />
      ) : (
        <DialogTitle>Interrupted import</DialogTitle>
      )}
    </Dialog>
  );
}

export default ResumeOrStartImportDialog;
